import { generateShortId } from '@o2d/shared';
import { creationDefaults } from '../../core/viewport/zoom-cluster.service.js';
import { MIN_LINE_LENGTH } from '../../constants/whiteboard-config.constants.js';
import type { LineObject, LineCap, LineAnchor } from './line.interface.js';

const DEFAULT_STROKE_COLOR = '#374151';
const DEFAULT_STROKE_WIDTH = 2;
const DEFAULT_START_CAP: LineCap = 'none';
const DEFAULT_END_CAP: LineCap = 'arrow';

/**
 * Optional caller-supplied overrides — anchors are typically populated by
 * the LineLifecycleController (T18) after running `findSnap` on both
 * endpoints; styling overrides exist for future templating needs.
 */
export interface CreateLineDefaultsOptions {
  readonly startAnchor?: LineAnchor;
  readonly endAnchor?: LineAnchor;
  readonly strokeWidth?: number;
  readonly strokeColor?: string;
  readonly startCap?: LineCap;
  readonly endCap?: LineCap;
  readonly pathStrategy?: string;
  /** Stefan, 2026-09-03 — "choose properties before drawing" (Bugreport
   *  Anke: "Linienart (gestrichelt etc.)" fehlte). No hardcoded default (the
   *  object contract itself renders unset as solid) — mirrors
   *  `startAnchor`/`endAnchor`/`pathStrategy`'s conditional-spread pattern
   *  below rather than a `??` fallback. */
  readonly strokeStyle?: 'solid' | 'dashed' | 'dotted';
  /** Bugreport (Review-Lauf 01, 2026-09-03): the shadow toggle staged
   *  correctly but was never read at placement time. No hardcoded default
   *  (unset = no shadow), conditional-spread only. */
  readonly boxShadow?: 'on';
}

/**
 * Build a fresh `LineObject` payload — extracted from the inline
 * `placeLineAt` body in `o2d-whiteboard.element.ts` per PRD-F5 TR17.
 *
 * Zero-length protection: when `to` is omitted, or the distance between
 * `from` and `to` is below `MIN_LINE_LENGTH`, the line falls back to a
 * horizontal line whose length scales with the viewport cluster factor —
 * mirrors the legacy `placeLineAt` behaviour so a bare click never
 * produces a degenerate zero-size object.
 *
 * `x,y,width,height` are derived from the endpoints (`min` / `abs`) so
 * the bounding box invariant of `BoardObjectBase` holds.
 */
export function createLineDefaults(
  viewportScale: number,
  from: { x: number; y: number },
  to?: { x: number; y: number } | null,
  options?: CreateLineDefaultsOptions,
): LineObject {
  const x1 = from.x;
  const y1 = from.y;
  let x2: number;
  let y2: number;
  if (to == null) {
    const cd = creationDefaults(viewportScale);
    x2 = x1 + cd.lineLength;
    y2 = y1;
  } else {
    const dx = to.x - x1;
    const dy = to.y - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < MIN_LINE_LENGTH) {
      const cd = creationDefaults(viewportScale);
      x2 = x1 + cd.lineLength;
      y2 = y1;
    } else {
      x2 = to.x;
      y2 = to.y;
    }
  }

  return {
    id: generateShortId(),
    type: 'line',
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
    rotation: 0,
    x1,
    y1,
    x2,
    y2,
    strokeWidth: options?.strokeWidth ?? DEFAULT_STROKE_WIDTH,
    strokeColor: options?.strokeColor ?? DEFAULT_STROKE_COLOR,
    startCap: options?.startCap ?? DEFAULT_START_CAP,
    endCap: options?.endCap ?? DEFAULT_END_CAP,
    ...(options?.startAnchor !== undefined ? { startAnchor: options.startAnchor } : {}),
    ...(options?.endAnchor !== undefined ? { endAnchor: options.endAnchor } : {}),
    ...(options?.pathStrategy !== undefined ? { pathStrategy: options.pathStrategy } : {}),
    ...(options?.strokeStyle !== undefined ? { strokeStyle: options.strokeStyle } : {}),
    ...(options?.boxShadow !== undefined ? { boxShadow: options.boxShadow } : {}),
  };
}
